import { Alert, ArrowIcon } from "@cmsgov/ds-medicare-gov";
import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from "react";
import { RouteComponentProps, withRouter } from "react-router";
import { HashLink } from "react-router-hash-link";
import { AutocompletePapDrug, PapPlan } from "../../@types";
import { getPapPlansForDrug } from "../../api";
import { AnalyticsActionType } from "../../app/contexts/Analytics/types";
import routes from "../../app/routes";
import { AppContext } from "../../app/store";
import {
  AppPage,
  DrugAutocomplete,
  FocusableHeader,
  LoadingMask,
  PrintButton,
} from "../../components";
import { useTranslate } from "../../helpers";
import PapPlanCard from "./PapPlanCard";

interface PapPlansMatchParams {
  drug: string;
}

const PapPlansPage: FunctionComponent<RouteComponentProps> = ({
  history,
  match,
  location,
}) => {
  const t = useTranslate();
  const { dispatch } = useContext(AppContext);
  const [plans, setPlans] = useState<PapPlan[]>([]);
  const [drug, setDrug] = useState<AutocompletePapDrug>({
    id: (match.params as PapPlansMatchParams).drug,
    name: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchPlans = async () => {
    setLoading(true);
    setError("");
    try {
      const { drugName, programs } = await getPapPlansForDrug(drug.id);
      setLoading(false);
      setPlans(programs);
      setDrug({ id: drug.id, name: drugName });
    } catch (_e) {
      setError(t("pap.plans.error"));
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, [drug.id]);

  useEffect(() => {
    setDrug({
      id: (match.params as PapPlansMatchParams).drug,
      name: drug.name,
    });
  }, [match.params]);

  return (
    <AppPage hideLogin={true} innerClassName="PapPlansPage">
      <LoadingMask loading={loading} delay={500} />
      <div className="PapPlansPage__banner ds-u-fill--gray-lightest ds-u-display--flex ds-u-flex-wrap--wrap ds-u-justify-content--between mct-hide-print ds-u-padding--2 ds-u-md-padding-x--7 ds-u-md-padding-y--2 ds-u-margin-bottom--2">
        <DrugAutocomplete
          onSelectDrug={selectedDrug => {
            history.push(
              `${routes.pap.plans.replace(
                ":drug",
                (selectedDrug as AutocompletePapDrug).id
              )}${location.search}`
            );
          }}
          PAP
          submitButtonText={t("pap.see_programs")}
        />

        <PrintButton
          tealiumProps={{
            event_action: "Print",
            event_label: window.location.hash,
            other_props: { event_category: "PAP" },
          }}
        />
      </div>
      <div className="ds-u-border-bottom--1 ds-u-padding-bottom--2 mct-hide-print">
        <HashLink
          to={`${routes.pap.landingPage}${location.search}`}
          onClick={(): void => {
            dispatch({
              type: AnalyticsActionType.SEND_TEALIUM_EVENT,
              settings: {
                event_action: "Back to search",
                event_label: window.location.hash,
                other_props: { event_category: "PAP" },
              },
            });
          }}
          className="ds-c-button ds-c-button--ghost ds-u-text-decoration--none ds-u-padding-left--0"
        >
          <ArrowIcon
            direction="left"
            className="ds-u-valign--middle ds-u-margin-right--1"
          />
          {t("pap.plans.go_back")}
        </HashLink>
        <FocusableHeader
          className="PapPlansPage__h1"
          text={
            error || !drug.name
              ? t("pap.plans.search_for_drug")
              : {
                  id: "pap.plans.viewing_number_results",
                  values: {
                    numberResults: plans.length.toString(),
                    drugName: drug.name,
                  },
                }
          }
        />
      </div>
      <div className="PapPlansPage__content">
        {error && (
          <div className="ds-u-margin-y--2">
            <Alert variation="error" role="alert">
              {error}
            </Alert>
          </div>
        )}
        <ul
          className="ds-c-list--bare"
          role="list"
          data-testid="e2e-program-results"
        >
          {plans.map(plan => {
            return (
              <li key={plan.program_name} role="listitem">
                <PapPlanCard papPlan={plan} drugName={drug.name} />
              </li>
            );
          })}
        </ul>
      </div>
    </AppPage>
  );
};

export default withRouter(PapPlansPage);
